import { useProductStore } from '../../products/stores/productStore';
import type { StorageArea } from '../domain/storageArea';
import { isBulkStorage, isLogYard } from '../domain/storageAreaType';

export function calculateFillLevelVolume(storageArea: StorageArea): number | null {
  if (isLogYard(storageArea.type)) {
    return calculateLogYardFillLevel(storageArea);
  }
  if (isBulkStorage(storageArea.type)) {
    return calculateBulkFillLevelVolume(storageArea);
  }
  return null;
}

export function calculateFillLevelWeight(storageArea: StorageArea): number | null {
  if (isLogYard(storageArea.type)) {
    return null;
  }
  if (isBulkStorage(storageArea.type)) {
    return calculateBulkFillLevelWeight(storageArea);
  }
  return null;
}

function calculateLogYardFillLevel(storageArea: StorageArea): number {
  const productStore = useProductStore();
  if (storageArea.logYardData) {
    const logYardContents = storageArea.logYardData.content;
    let filledSideArea = 0.0;
    for (const content of logYardContents) {
      const product = productStore.getById(content.productId);
      if (product?.logData?.logsPerSquareMeter && product.logData.logsPerSquareMeter > 0) {
        filledSideArea += content.numberOfLogs / product?.logData.logsPerSquareMeter;
      }
    }

    if (storageArea.logYardData.sideArea && storageArea.logYardData.sideArea > 0) {
      return Math.round(Math.min((filledSideArea / storageArea.logYardData.sideArea) * 100.0, 100));
    }
  }

  return 0;
}

function calculateBulkFillLevelWeight(storageArea: StorageArea): number {
  const bulkStorageData = storageArea.bulkStorageData;

  if (!bulkStorageData?.maxWeight || bulkStorageData.maxWeight <= 0) {
    return 0;
  }

  const filledWeight = bulkStorageData.content.reduce((sum, content) => sum + content.ton, 0);

  return Math.round(Math.min((filledWeight / bulkStorageData.maxWeight) * 100, 100));
}

function calculateBulkFillLevelVolume(storageArea: StorageArea): number {
  const productStore = useProductStore();
  const bulkStorageData = storageArea.bulkStorageData;
  if (!bulkStorageData?.maxVolume || bulkStorageData.maxVolume <= 0) {
    return 0;
  }

  let filledVolume = 0;

  for (const content of bulkStorageData.content) {
    const product = productStore.getById(content.productId);
    const density = product?.bulkData?.density;

    if (density && density > 0) {
      const massKg = content.ton * 1000; // tons → kg
      filledVolume += massKg / density; // m³
    }
  }

  return Math.round(Math.min((filledVolume / bulkStorageData.maxVolume) * 100, 100));
}
